import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../Context/ChartContext/ChartContext";
import MUIBox from "@mui/material/Box";
import MUICard from "@mui/material/Card";
import MUIList from "@mui/material/List";
import MUIListItemButton from "@mui/material/ListItemButton";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIListItemText from "@mui/material/ListItemText";
import MUIIconButton from "@mui/material/IconButton";
import MUITooltip from "@mui/material/Tooltip";
import MUIChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MUIChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha } from "@mui/material/styles";
import Setting from "../Utility/Setting";

const NAV_EXPANDED_WIDTH = 156;
const NAV_COLLAPSED_WIDTH = 58;

export default function ChartNavigationBar({
  mode,
  orderId,
  rightColumnCards,
  cardComponentMap,
  getCardAnchorKey,
  cardAnchorRefs,
  leftColumnScrollContainerRef,
  rightColumnScrollContainerRef,
  scrollToCard,
}) {
  const [navigationExpanded, setNavigationExpanded] = useState(
    () => Setting.get("chart.navigationExpanded") !== false,
  );
  const [activeOrderCardAnchorKey, setActiveOrderCardAnchorKey] =
    useState(null);

  const chartContext = useContext(ChartContext);
  const orderCardNavItems =
    mode === "viewOrderActivity"
      ? rightColumnCards
          .map((card, cardIndex) => ({ card, cardIndex }))
          .filter(
            ({ card }) =>
              cardComponentMap[card.name]?.meta?.showInNavigation !== false,
          )
          .map(({ card, cardIndex }) => ({
            cardAnchorKey: getCardAnchorKey("right", card.name, cardIndex),
            cardName: card.name,
            label: cardComponentMap[card.name].meta.title,
            icon: cardComponentMap[card.name].meta.icon,
          }))
      : [];
  const navCardKeySignature = orderCardNavItems
    .map((cardNavItem) => cardNavItem.cardAnchorKey)
    .join("|");
  const navigationWidth = navigationExpanded
    ? NAV_EXPANDED_WIDTH
    : NAV_COLLAPSED_WIDTH;

  useEffect(() => {
    Setting.set("chart.navigationExpanded", navigationExpanded);
  }, [navigationExpanded]);

  useEffect(() => {
    if (!orderId || mode !== "viewOrderActivity") {
      setActiveOrderCardAnchorKey(null);
      return;
    }

    let animationFrameRequestId = null;

    const getCardEntries = (columnName = null) => {
      return Object.entries(cardAnchorRefs.current).filter((entry) => {
        const cardAnchorKey = entry[0];
        const element = entry[1];

        if (!element || !element.isConnected) {
          return false;
        }

        if (!columnName) {
          return true;
        }

        return cardAnchorKey.startsWith(`${columnName}-`);
      });
    };

    const getTopThreshold = (containerElement = null) => {
      if (!containerElement) {
        return 100;
      }

      const rect = containerElement.getBoundingClientRect();
      return rect.top + 100;
    };

    const pickClosestVisibleCard = (cardEntries, topThreshold) => {
      if (cardEntries.length === 0) {
        return null;
      }

      let closestAboveThreshold = null;
      let closestAboveThresholdTop = Number.NEGATIVE_INFINITY;
      let closestBelowThreshold = null;
      let closestBelowThresholdTop = Number.POSITIVE_INFINITY;

      for (
        let cardEntryIndex = 0;
        cardEntryIndex < cardEntries.length;
        cardEntryIndex++
      ) {
        const cardAnchorKey = cardEntries[cardEntryIndex][0];
        const element = cardEntries[cardEntryIndex][1];
        const rect = element.getBoundingClientRect();

        if (rect.top <= topThreshold && rect.top > closestAboveThresholdTop) {
          closestAboveThresholdTop = rect.top;
          closestAboveThreshold = cardAnchorKey;
        }

        if (rect.top > topThreshold && rect.top < closestBelowThresholdTop) {
          closestBelowThresholdTop = rect.top;
          closestBelowThreshold = cardAnchorKey;
        }
      }

      return closestAboveThreshold || closestBelowThreshold;
    };

    const updateActiveOrderCard = (updateContext = null) => {
      let columnName = null;
      let containerElement = null;

      if (updateContext) {
        if (updateContext.columnName !== undefined) {
          columnName = updateContext.columnName;
        }
        if (updateContext.containerElement !== undefined) {
          containerElement = updateContext.containerElement;
        }
      }

      const cardEntries = getCardEntries(columnName);
      if (cardEntries.length === 0 && columnName) {
        return updateActiveOrderCard();
      }

      if (cardEntries.length === 0) {
        return;
      }

      const topThreshold = getTopThreshold(containerElement);
      const nextActiveAnchorKey = pickClosestVisibleCard(
        cardEntries,
        topThreshold,
      );

      if (nextActiveAnchorKey) {
        setActiveOrderCardAnchorKey((previousActiveAnchorKey) =>
          previousActiveAnchorKey === nextActiveAnchorKey
            ? previousActiveAnchorKey
            : nextActiveAnchorKey,
        );
      }
    };

    const queueActiveOrderCardUpdate = (updateContext = {}) => {
      if (animationFrameRequestId !== null) {
        window.cancelAnimationFrame(animationFrameRequestId);
      }

      animationFrameRequestId = window.requestAnimationFrame(() => {
        animationFrameRequestId = null;
        updateActiveOrderCard(updateContext);
      });
    };

    const leftColumnElement = leftColumnScrollContainerRef.current;
    const rightColumnElement = rightColumnScrollContainerRef.current;

    const onWindowScrollOrResize = () => queueActiveOrderCardUpdate();
    const onLeftColumnScroll = () =>
      queueActiveOrderCardUpdate({
        columnName: "left",
        containerElement: leftColumnElement,
      });
    const onRightColumnScroll = () =>
      queueActiveOrderCardUpdate({
        columnName: "right",
        containerElement: rightColumnElement,
      });

    window.addEventListener("scroll", onWindowScrollOrResize, {
      passive: true,
    });
    window.addEventListener("resize", onWindowScrollOrResize);
    leftColumnElement?.addEventListener("scroll", onLeftColumnScroll, {
      passive: true,
    });
    rightColumnElement?.addEventListener("scroll", onRightColumnScroll, {
      passive: true,
    });

    queueActiveOrderCardUpdate({
      columnName: "right",
      containerElement: rightColumnElement,
    });

    return () => {
      if (animationFrameRequestId !== null) {
        window.cancelAnimationFrame(animationFrameRequestId);
      }
      window.removeEventListener("scroll", onWindowScrollOrResize);
      window.removeEventListener("resize", onWindowScrollOrResize);
      leftColumnElement?.removeEventListener("scroll", onLeftColumnScroll);
      rightColumnElement?.removeEventListener("scroll", onRightColumnScroll);
    };
  }, [
    orderId,
    mode,
    navCardKeySignature,
    cardAnchorRefs,
    leftColumnScrollContainerRef,
    rightColumnScrollContainerRef,
  ]);

  if (orderCardNavItems.length === 0) {
    return null;
  }

  return (
    <MUIBox
      sx={{
        flex: { xs: 1, sm: `0 0 ${navigationWidth}px` },
        minWidth: { xs: 0, sm: navigationWidth },
        maxWidth: { xs: "none", sm: navigationWidth },
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        py: 0.5,
      }}
    >
      <MUICard
        variant="outlined"
        sx={(theme) => ({
          p: 0.25,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          boxShadow: `inset 0 0 3px ${alpha(theme.palette.common.black, 0.14)}`,
        })}
      >
        <MUIBox
          sx={{
            flex: 1,
            overflowY: { xs: "visible", sm: "auto" },
          }}
        >
          <MUIList disablePadding dense>
            {orderCardNavItems.map((cardNavItem) => {
              const CardNavIcon = cardNavItem.icon;
              const isInEditMode =
                chartContext?.cardsInEditMode?.[cardNavItem.cardName];
              const hasValidationIssues =
                chartContext?.validationIssues?.[cardNavItem.cardName] &&
                Object.keys(chartContext.validationIssues[cardNavItem.cardName])
                  .length > 0;

              return (
                <MUITooltip
                  key={cardNavItem.cardAnchorKey}
                  title={!navigationExpanded ? cardNavItem.label : ""}
                  placement="left"
                >
                  <MUIListItemButton
                    selected={
                      cardNavItem.cardAnchorKey === activeOrderCardAnchorKey
                    }
                    onClick={() => scrollToCard(cardNavItem.cardAnchorKey)}
                    sx={(theme) => {
                      const accentColor = hasValidationIssues
                        ? theme.palette.error.main
                        : isInEditMode
                          ? theme.palette.success.main
                          : null;
                      const baseStyles = {
                        borderRadius: 1,
                        minHeight: 36,
                        p: 0.5,
                        mb: 0.125,
                        justifyContent: navigationExpanded
                          ? "flex-start"
                          : "center",
                        border: "1px solid transparent",
                        borderLeftWidth: "3px",
                        borderLeftColor: accentColor
                          ? accentColor
                          : "transparent",
                        ...(accentColor && {
                          backgroundColor: alpha(accentColor, 0.12),
                          "&:hover": {
                            backgroundColor: alpha(
                              accentColor,
                              isInEditMode && !hasValidationIssues ? 0.28 : 0.2,
                            ),
                          },
                        }),
                        "&.Mui-selected": {
                          ...(accentColor
                            ? {
                                borderColor: accentColor,
                                borderLeftColor: accentColor,
                                backgroundColor: alpha(accentColor, 0.2),
                              }
                            : {
                                borderColor: "primary.main",
                              }),
                        },
                        "& .MuiListItemIcon-root": {
                          color: "text.secondary",
                        },
                        "&.Mui-selected .MuiListItemIcon-root": {
                          color: "primary.main",
                        },
                      };
                      return baseStyles;
                    }}
                  >
                    <MUIListItemIcon
                      sx={{ minWidth: navigationExpanded ? 24 : 0 }}
                    >
                      <CardNavIcon sx={{ fontSize: "1rem" }} />
                    </MUIListItemIcon>
                    {navigationExpanded && (
                      <MUIListItemText
                        sx={{ minWidth: 0 }}
                        primary={cardNavItem.label}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            fontSize: "0.8rem",
                            noWrap: true,
                          },
                        }}
                      />
                    )}
                  </MUIListItemButton>
                </MUITooltip>
              );
            })}
          </MUIList>
        </MUIBox>
        <MUIBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            py: 0.25,
          }}
        >
          <MUITooltip
            title={navigationExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <MUIIconButton
              size="small"
              onClick={() => setNavigationExpanded((isExpanded) => !isExpanded)}
              aria-label={
                navigationExpanded ? "Collapse sidebar" : "Expand sidebar"
              }
            >
              {navigationExpanded ? (
                <MUIChevronRightIcon fontSize="small" />
              ) : (
                <MUIChevronLeftIcon fontSize="small" />
              )}
            </MUIIconButton>
          </MUITooltip>
        </MUIBox>
      </MUICard>
    </MUIBox>
  );
}
